import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Plus } from "lucide-react";
import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import type { TrainingModuleSummary } from "@xop/dashboard-shared";

import { useCan } from "../../../auth/permissions.js";
import { PageHeader } from "../../../components/PageHeader.js";
import { EmptyState } from "../../../components/ui/empty-state.js";
import { TRAINING_LIST_KEY, listTrainingModules, trainingErrorMessage, trainingModuleKey } from "./api.js";
import { PublishedChip } from "./ModuleCategory.js";
import { NewModuleDialog } from "./NewModuleDialog.js";
import { groupModules, moduleStats } from "./trainingLogic.js";

/**
 * Documentation ▸ Online Training — the modules resellers learn from.
 *
 * One card per module, grouped Services ∣ Extensions (the order the reseller
 * portal shows them in). A card says what an author needs before opening it:
 * how many videos it has, how long it runs, and whether resellers can see it
 * yet. Creating one is a three-field dialog; everything else happens on the
 * module's own page. Readers (`documentation:view`) see the list; only a full
 * editor (`documentation:edit`) gets "New module" — portal-api refuses the
 * write for anyone else anyway.
 */
export function OnlineTrainingListPage(): JSX.Element {
  const canEdit = useCan("documentation:edit");
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: TRAINING_LIST_KEY,
    queryFn: listTrainingModules,
  });

  const groups = groupModules(data ?? []);
  const empty = !isLoading && !error && (data?.length ?? 0) === 0;

  return (
    <Box data-testid="training-list-page">
      <PageHeader
        title="Online Training"
        subtitle="Video courses for resellers — one module per service or extension, a short video per topic."
        right={
          canEdit ? (
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => setCreating(true)}
              data-testid="training-new-module"
            >
              New module
            </Button>
          ) : undefined
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{trainingErrorMessage(error)}</Alert>}

      {isLoading && (
        <Box sx={GRID_SX}>
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} variant="rounded" sx={{ height: 148 }} />
          ))}
        </Box>
      )}

      {empty && (
        <EmptyState
          icon={<Clapperboard size={22} />}
          title="No training modules yet"
          description={
            canEdit
              ? "Create a module for a service or an extension, then build its topic menu and add a video to each topic."
              : "Nothing has been published here yet."
          }
          action={
            canEdit ? (
              <Button variant="outlined" onClick={() => setCreating(true)}>
                Create the first module
              </Button>
            ) : undefined
          }
        />
      )}

      <Stack spacing={4}>
        <ModuleGroup title="Services" testId="training-group-services" modules={groups.services} />
        <ModuleGroup title="Extensions" testId="training-group-extensions" modules={groups.extensions} />
      </Stack>

      <NewModuleDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(m) => {
          setCreating(false);
          qc.setQueryData(trainingModuleKey(m.id), m);
          void qc.invalidateQueries({ queryKey: TRAINING_LIST_KEY });
          navigate(`/documentation/online-training/${m.id}`);
        }}
      />
    </Box>
  );
}

const GRID_SX = {
  display: "grid",
  gap: 2,
  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", xl: "repeat(3, minmax(0, 1fr))" },
} as const;

function ModuleGroup({
  title,
  testId,
  modules,
}: {
  title: string;
  testId: string;
  modules: TrainingModuleSummary[];
}): JSX.Element | null {
  if (modules.length === 0) return null;
  return (
    <Box component="section" data-testid={testId}>
      <Typography
        variant="overline"
        component="h2"
        sx={{ display: "block", color: "text.secondary", fontWeight: 600, letterSpacing: "0.08em", mb: 1 }}
      >
        {title} · {modules.length}
      </Typography>
      <Box sx={GRID_SX}>
        {modules.map((m) => (
          <ModuleCard key={m.id} module={m} />
        ))}
      </Box>
    </Box>
  );
}

function ModuleCard({ module: m }: { module: TrainingModuleSummary }): JSX.Element {
  return (
    <Card variant="outlined" data-testid={`training-module-card-${m.id}`} sx={{ height: "100%" }}>
      <CardActionArea
        component={RouterLink}
        to={`/documentation/online-training/${m.id}`}
        sx={{ height: "100%", alignItems: "stretch" }}
      >
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1, height: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3 }}>
              {m.title}
            </Typography>
            <PublishedChip published={m.published} />
          </Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              flex: 1,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: "2.6em",
            }}
          >
            {m.summary || "No summary yet."}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "flex", alignItems: "center", gap: 0.75, fontWeight: 500 }}
            data-testid="training-module-stats"
          >
            <Clapperboard size={14} />
            {moduleStats(m)}
          </Typography>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
